import type { FC } from 'react';
import { SessionManagementWrapper } from './SessionManagement.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface SessionManagementProps { }

const SessionManagement: FC<SessionManagementProps> = () => (
   <SessionManagementWrapper>
      <header className="entry-header ">
         <h1 className="entry-title" itemProp="headline">Session Management</h1>
      </header>
      <br/>
      <div className="entry-content clear" itemProp="text" style={{ height: 'auto !important' }}>
         <p className="text-align-justify">A session contains information specific to a particular user across the whole application and consists of multiple requests and responses between client and server. Since HTTP and Web Server both are stateless(consider each request as a new request), the only way to maintain a session is when we pass some unique information about the session (session-id) between server and client in every request and response.</p>
         <p className="text-align-justify">To maintain the state (data) of a user, we use Session Tracking. In other words, it is referred to as session management in servlet. </p>
         <br/>
         <h3 className="text-align-justify">Session tracking uses four techniques as following :</h3>
         <br/>
         <h4 className="text-align-justify"><strong>Cookies</strong></h4>
         <ul className="text-align-justify">
            <li>Cookies are just a piece of information, the webserver sends it in response header and it gets stored in the browser cookies. </li>
            <li>When a further request is made by the client, it adds the cookie to the request header and we can utilize it to keep track of the session. </li>
            <li>In addition, there is no method available to remove the cookie, however, we can set the maximum age to 0. In other words, it will be deleted from the client’s browser immediately.</li>
            <li>It doesn’t work if the client disables cookies.</li>
            <li>If the cookies are disabled at the client-side and we are using URL rewriting then this method uses the sessionid value from the request URL to find the corresponding session.</li>
            <br/>
            <li><strong>Types of cookies :</strong>
               <ul>
                  <li>The non-persistent cookie is valid for a single session only. Each time the user closes the browser, it is removed.</li>
                  <li>A persistent cookie is valid for multiple sessions. Each time when the user closes the browser, it is not removed. Instead only removed if the user logs out or signs out.</li>
               </ul>
            </li>
            <br/>
            <li><strong>Methods available for cookies :</strong>
               <ul>
                  <li><strong>addCookie(Cookie c)</strong> : It adds cookie in the response object.</li>
                  <li><strong>getCookies()</strong> : It returns all the cookies from the browser.</li>
                  <li><strong>setMaxAge(int expiry)</strong> : It sets the maximum age of the cookie in seconds.</li>
               </ul>
            </li>
            <li>Eg :
               <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                  {
                     'Cookie ck = new Cookie("user", "");\n'+
                     'ck.setMaxAge(0);\n'+
                     'response.addCookie(ck);\n'+
                     'Cookie ck[] = request.getCookies();'
                  }
               </SyntaxHighlighter>
            </li>
         </ul>
         <br/>
         <h4 className="text-align-justify"><strong>Hidden Form Field</strong> </h4>
         <ul className="text-align-justify">
            <li>Inside out HTML, we can create a unique hidden field and we can set its value unique to the user when the user starts navigating and keep track of the session.</li>
            <li>It is not a secure method.</li>
            <li>However, this approach is better if we want to submit the form in all the pages, but we don’t want to depend on the browser.</li>
            <li>Eg :
               <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                  {
                     '<input type="hidden" name="name" value="Ram">'
                  }
               </SyntaxHighlighter>
            </li>
         </ul>
         <br/>
         <h4 className="text-align-justify"><strong>URL Rewriting</strong> </h4>
         <ul className="text-align-justify">
            <li>With every request and response, we can append a session identifier parameter to keep track of the session.</li>
            <li>It’s a very easy technique to use as it involves just one step – encoding the URL.</li>
            <li>It’s a fallback approach and it kicks in only if we disable the browser cookies.</li>
            <li>We can encode URL with HttpServletResponse encodeURL() and if we have to redirect the request to another resource and we want to provide session information, we can use the encodeRedirectURL() method.</li>
            <li>Just send the parameters as name/value pairs using the following format: url?name1=value1&amp;name2=value2&amp;??</li>
            <li>Eg :
               <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                  {
                     '<a href=\' Servlet?name=" + n + "\'> visit </a>'
                  }
               </SyntaxHighlighter>
            </li>
         </ul>
         <br/>
         <h4 className="text-align-justify"><strong>HttpSession</strong> </h4>
         <ul className="text-align-justify">
            <li>HttpSession allows us to set objects as attributes that can be retrieved in future requests.</li>
            <li><strong>It can perform the below tasks:</strong>
               <ul>
                  <li>bind objects</li>
                  <li>view and change information about a session. For instance the session identifier, creation time, or last accessed time.</li>
               </ul>
            </li>
            <br/>
            <li><strong>Methods available for HttpSession :</strong>
               <ul>
                  <li><strong>HttpSession getSession() :</strong>
                     <ul>
                        <li>This method always returns a HttpSession object. </li>
                        <li>Attached to the request, it will return the session object. And it creates a new session if the request has no session attached and return it.</li>
                     </ul>
                  </li>
                  <li><strong>HttpSession getSession(boolean flag) :</strong>
                     <ul>
                        <li>If the request has a session, this method will return a HttpSession object.</li>
                        <li>If the request has no session attached, then it returns null.</li>
                     </ul>
                  </li>
               </ul>
            </li>
            <li>Eg :
               <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                  {
                     'HttpSession session=request.getSession(false);  \n' +
                     'String n=(String)session.getAttribute("name");'
                  }
               </SyntaxHighlighter>
            </li>
         </ul>
      </div>
   </SessionManagementWrapper>
);

export default SessionManagement;
